import { useCallback } from 'react';

interface RoleAssignment {
  id: string;
  userId?: string | null;
  roleType: string;
}

interface AssignmentItem {
  id: string;
  roleType: string;
  groupId?: string;
}

interface OperationBatch {
  create: Array<{
    userId?: string;
    groupId?: string;
    roleType: string;
    objectType: string;
    dossierId?: string;
  }>;
  update: Array<{
    id: string;
    roleType: string;
    userId?: string;
    groupId?: string;
    objectType: string;
    dossierId?: string;
  }>;
  delete: Array<{ id: string }>;
}

function getOriginalMaps(originalAssignments: RoleAssignment[]) {
  const originalUserMap = new Map(
    originalAssignments.filter((a) => a.userId).map((a) => [a.userId, a]),
  );

  const originalGroupMap = new Map(
    originalAssignments
      .filter((a) => (a as unknown as { groupId?: string }).groupId)
      .map((a) => [(a as unknown as { groupId?: string }).groupId, a]),
  );

  return { originalUserMap, originalGroupMap };
}

function collectDeletions(
  originalUserMap: Map<string | null | undefined, RoleAssignment>,
  originalGroupMap: Map<string | undefined, RoleAssignment>,
  targetUserMap: Map<string, AssignmentItem>,
  targetGroupMap: Map<string | undefined, AssignmentItem>,
): Array<{ id: string }> {
  const deletions: Array<{ id: string }> = [];

  for (const [userId, original] of originalUserMap) {
    if (userId && !targetUserMap.has(userId) && original.id) {
      deletions.push({ id: original.id });
    }
  }

  for (const [groupId, original] of originalGroupMap) {
    if (!targetGroupMap.has(groupId) && original.id) {
      deletions.push({ id: original.id });
    }
  }

  return deletions;
}

function collectAdditions(
  targetUsers: AssignmentItem[],
  targetGroups: AssignmentItem[],
  originalUserMap: Map<string | null | undefined, RoleAssignment>,
  originalGroupMap: Map<string | undefined, RoleAssignment>,
  objectType: string,
  objectId: string | undefined,
): OperationBatch['create'] {
  const additions: OperationBatch['create'] = [];

  for (const user of targetUsers) {
    if (!originalUserMap.has(user.id)) {
      additions.push({
        userId: user.id,
        roleType: user.roleType,
        objectType,
        ...(objectId ? { dossierId: objectId } : {}),
      });
    }
  }

  for (const group of targetGroups) {
    if (!group.groupId) {
      continue;
    }
    if (!originalGroupMap.has(group.groupId)) {
      additions.push({
        groupId: group.groupId,
        roleType: group.roleType,
        objectType,
        ...(objectId ? { dossierId: objectId } : {}),
      });
    }
  }

  return additions;
}

function collectUpdates(
  targetUsers: AssignmentItem[],
  targetGroups: AssignmentItem[],
  originalUserMap: Map<string | null | undefined, RoleAssignment>,
  originalGroupMap: Map<string | undefined, RoleAssignment>,
  objectType: string,
  objectId: string | undefined,
): OperationBatch['update'] {
  const updates: OperationBatch['update'] = [];

  for (const user of targetUsers) {
    const original = originalUserMap.get(user.id);
    if (!original || original.roleType === user.roleType || !original.id) {
      continue;
    }

    updates.push({
      id: original.id,
      roleType: user.roleType,
      userId: user.id,
      objectType,
      ...(objectId ? { dossierId: objectId } : {}),
    });
  }

  for (const group of targetGroups) {
    if (!group.groupId) {
      continue;
    }

    const original = originalGroupMap.get(group.groupId);
    if (!original || original.roleType === group.roleType || !original.id) {
      continue;
    }

    updates.push({
      id: original.id,
      roleType: group.roleType,
      groupId: group.groupId,
      objectType,
      ...(objectId ? { dossierId: objectId } : {}),
    });
  }

  return updates;
}

export function useSaveRoleAssignments() {
  const save = useCallback(
    (
      originalAssignments: RoleAssignment[],
      targetUsers: AssignmentItem[],
      targetGroups: AssignmentItem[],
      objectType: string,
      objectId: string | undefined,
    ): OperationBatch => {
      const { originalUserMap, originalGroupMap } =
        getOriginalMaps(originalAssignments);

      const targetUserMap = new Map(targetUsers.map((u) => [u.id, u]));
      const targetGroupMap = new Map(targetGroups.map((g) => [g.groupId, g]));

      const deletions = collectDeletions(
        originalUserMap,
        originalGroupMap,
        targetUserMap,
        targetGroupMap,
      );

      const additions = collectAdditions(
        targetUsers,
        targetGroups,
        originalUserMap,
        originalGroupMap,
        objectType,
        objectId,
      );

      const updates = collectUpdates(
        targetUsers,
        targetGroups,
        originalUserMap,
        originalGroupMap,
        objectType,
        objectId,
      );

      return {
        create: additions,
        update: updates,
        delete: deletions,
      };
    },
    [],
  );

  return { save };
}
